import { API_BASE_URL } from './api.js';
import { DomainApiError } from './api-error.js';

/**
 * Krok DEV1 — developerske projekty nad `/v1/developer-projects*`.
 *
 * Raw `fetch` (ne generovany `@reos/api-client`): routy jeste nejsou
 * v `openapi.json`, ktery regeneruje CI — stejny vzor a stejny duvod jako
 * `commissions-api.ts` (PROV1) a `acquisition-radar-api.ts` (AKV1).
 */

export type DeveloperUnitStatus = 'AVAILABLE' | 'RESERVED' | 'SOLD';
export type DeveloperUnitKind =
  'APARTMENT' | 'HOUSE' | 'GARAGE' | 'PARKING' | 'CELLAR' | 'COMMERCIAL' | 'LAND' | 'OTHER';
export type DeveloperProjectStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';

export interface DeveloperProjectSummaryDto {
  readonly unitCount: number;
  readonly available: number;
  readonly reserved: number;
  readonly sold: number;
  readonly priceFrom: string | null;
  readonly priceTo: string | null;
  readonly currency: string | null;
}

export interface DeveloperProjectDto {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly annotation: string | null;
  readonly description: string | null;
  readonly address: string | null;
  readonly locality: string | null;
  readonly developerIc: string | null;
  readonly completionDate: string | null;
  readonly projectUrl: string | null;
  readonly status: DeveloperProjectStatus;
}

export interface DeveloperProjectListItemDto extends DeveloperProjectDto {
  readonly summary: DeveloperProjectSummaryDto;
}

export interface DeveloperUnitDto {
  readonly id: string;
  readonly projectId: string;
  readonly label: string;
  readonly unitKind: DeveloperUnitKind;
  readonly layout: string | null;
  readonly floor: number | null;
  readonly areaM2: string | null;
  readonly price: string | null;
  readonly priceCurrency: string;
  readonly status: DeveloperUnitStatus;
  readonly phase: string | null;
  readonly caseId: string | null;
  readonly propertyId: string | null;
  readonly note: string | null;
}

export interface UnitsResponseDto {
  readonly project: DeveloperProjectDto;
  readonly items: readonly DeveloperUnitDto[];
  readonly summary: DeveloperProjectSummaryDto;
}

export interface ImportUnitsResultDto {
  readonly created: number;
  readonly updated: number;
  readonly problems: readonly {
    readonly row: number;
    readonly reason: string;
    readonly value: string;
  }[];
  readonly items: readonly DeveloperUnitDto[];
}

export interface BulkUnitsResultDto {
  readonly updated: number;
  readonly listingMarked: readonly { readonly unitId: string; readonly caseId: string }[];
  readonly listingSkipped: readonly { readonly unitId: string; readonly reason: string }[];
  readonly items: readonly DeveloperUnitDto[];
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      ...(init?.body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(init?.headers as Record<string, string> | undefined),
    },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      detail?: string;
      title?: string;
      type?: string;
    } | null;
    const text = [body?.title, body?.detail]
      .filter((part): part is string => typeof part === 'string')
      .join(' ');
    throw new DomainApiError(
      text === '' ? `HTTP ${String(res.status)}` : text,
      res.status,
      body?.type,
    );
  }
  return (await res.json()) as T;
}

export async function listDeveloperProjects(): Promise<readonly DeveloperProjectListItemDto[]> {
  const result = await req<{ items: readonly DeveloperProjectListItemDto[] }>(
    '/v1/developer-projects',
  );
  return result.items;
}

export interface CreateProjectInput {
  readonly name: string;
  readonly locality?: string;
  readonly address?: string;
  readonly annotation?: string;
  readonly completionDate?: string;
}

export async function createDeveloperProject(
  input: CreateProjectInput,
): Promise<DeveloperProjectDto> {
  return req<DeveloperProjectDto>('/v1/developer-projects', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface UnitFilter {
  readonly status?: DeveloperUnitStatus | undefined;
  readonly phase?: string | undefined;
}

export async function listDeveloperProjectUnits(
  projectId: string,
  filter: UnitFilter = {},
): Promise<UnitsResponseDto> {
  const query = new URLSearchParams();
  if (filter.status !== undefined) query.set('status', filter.status);
  if (filter.phase !== undefined && filter.phase !== '') query.set('phase', filter.phase);
  const suffix = query.toString() === '' ? '' : `?${query.toString()}`;
  return req<UnitsResponseDto>(`/v1/developer-projects/${projectId}/units${suffix}`);
}

export async function importDeveloperProjectUnits(
  projectId: string,
  text: string,
): Promise<ImportUnitsResultDto> {
  return req<ImportUnitsResultDto>(`/v1/developer-projects/${projectId}/units`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

export interface BulkUnitsInput {
  readonly unitIds: readonly string[];
  readonly status?: DeveloperUnitStatus | undefined;
  readonly price?: string | undefined;
  readonly pricePercent?: number | undefined;
  readonly reason: string;
}

export async function bulkUpdateDeveloperProjectUnits(
  projectId: string,
  input: BulkUnitsInput,
): Promise<BulkUnitsResultDto> {
  return req<BulkUnitsResultDto>(`/v1/developer-projects/${projectId}/units`, {
    method: 'PATCH',
    body: JSON.stringify({
      unitIds: [...input.unitIds],
      ...(input.status !== undefined ? { status: input.status } : {}),
      ...(input.price !== undefined ? { price: input.price } : {}),
      ...(input.pricePercent !== undefined ? { pricePercent: input.pricePercent } : {}),
      reason: input.reason,
    }),
  });
}

/** Odkaz na cenik — stahuje prohlizec, ne fetch (soubor jde do Downloads). */
export function pricelistCsvUrl(projectId: string): string {
  return `${API_BASE_URL}/v1/developer-projects/${projectId}/pricelist`;
}
